import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ShieldCheck, AlertTriangle, CheckCircle, XCircle, ClipboardCheck,
  Plus, Loader2, X, ChevronDown, ChevronUp, Clock, Flame,
  Droplets, Beaker, FileWarning, ClipboardList,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiGet, apiPost } from '@/lib/api'
import { cn, formatDate, formatDateTime } from '@/lib/utils'

interface InspectionTemplate {
  id: string
  name: string
  category: string
  description: string | null
  frequency: string | null
  regulation: string | null
  items: { sequence: number; question: string; type: string; isMandatory: boolean; acceptableRange?: { min?: number; max?: number; unit?: string } }[]
}

interface InspectionRecord {
  id: string
  templateName: string
  category: string | null
  status: string
  overallResult: string | null
  completedAt: string | null
  nextDueDate: string | null
  notes: string | null
  correctiveAction: string | null
  items: { sequence: number; question: string; result: string | null; notes: string | null }[]
  template: { name: string; category: string; frequency: string | null } | null
  summary: { passCount: number; failCount: number; advisoryCount: number; totalItems: number }
}

interface SafetyLog {
  summary: {
    totalLast30Days: number
    failedLast30Days: number
    requiresAction: number
    overdueCount: number
    byCategory: Record<string, number>
  }
  overdueInspections: any[]
  templateStatus: {
    template: { id: string; name: string; category: string; frequency: string | null; regulation: string | null }
    lastInspection: { overallResult: string; completedAt: string; nextDueDate: string | null; status: string } | null
    isOverdue: boolean
    isDue: boolean
    neverCompleted: boolean
  }[]
}

const CATEGORY_CONFIG: Record<string, { label: string; icon: typeof Flame; color: string; bg: string }> = {
  FIRE_SAFETY: { label: 'Fire Safety', icon: Flame, color: 'text-red-600', bg: 'bg-red-100' },
  HACCP: { label: 'Food Safety', icon: ClipboardCheck, color: 'text-green-700', bg: 'bg-green-100' },
  POOL_SPA: { label: 'Pool & Spa', icon: Droplets, color: 'text-blue-600', bg: 'bg-blue-100' },
  LEGIONELLA: { label: 'Legionella', icon: Beaker, color: 'text-purple-600', bg: 'bg-purple-100' },
  COSHH: { label: 'COSHH', icon: FileWarning, color: 'text-amber-600', bg: 'bg-amber-100' },
  GENERAL: { label: 'General', icon: ClipboardList, color: 'text-gray-600', bg: 'bg-gray-100' },
}

const RESULT_CONFIG: Record<string, { label: string; class: string }> = {
  PASS: { label: 'Pass', class: 'bg-green-100 text-green-800' },
  FAIL: { label: 'Fail', class: 'bg-red-100 text-red-800' },
  ADVISORY: { label: 'Advisory', class: 'bg-amber-100 text-amber-800' },
  NOT_APPLICABLE: { label: 'N/A', class: 'bg-gray-100 text-gray-500' },
}

export function SafetyLogPage() {
  const { currentPropertyId, user } = useAuthStore()
  const pid = currentPropertyId
  const queryClient = useQueryClient()

  const [tab, setTab] = useState<'dashboard' | 'history' | 'templates'>('dashboard')
  const [showInspectModal, setShowInspectModal] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<InspectionTemplate | null>(null)
  const [inspectionResults, setInspectionResults] = useState<Record<number, { result: string; notes: string; value: string }>>({})
  const [inspectionNotes, setInspectionNotes] = useState('')
  const [correctiveAction, setCorrectiveAction] = useState('')
  const [filterCategory, setFilterCategory] = useState('')

  // ── Queries ─────────────────────────────────────────────────────────────
  const { data: safetyLog, isLoading: logLoading } = useQuery({
    queryKey: ['safety-log', pid],
    queryFn: () => apiGet<SafetyLog>(`/properties/${pid}/inspections/safety-log`),
    enabled: !!pid,
  })

  const { data: templates } = useQuery({
    queryKey: ['inspection-templates', filterCategory],
    queryFn: () => apiGet<InspectionTemplate[]>(
      `/properties/${pid}/inspections/templates`,
      filterCategory ? { category: filterCategory } : {}
    ),
    enabled: !!pid,
  })

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['inspections-history', pid, filterCategory],
    queryFn: () => apiGet<{ data: InspectionRecord[]; pagination: any }>(
      `/properties/${pid}/inspections`,
      { limit: 50, ...(filterCategory && { category: filterCategory }) }
    ),
    enabled: !!pid && tab === 'history',
  })

  // ── Submit inspection ───────────────────────────────────────────────────
  const submitMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost(`/properties/${pid}/inspections`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['safety-log'] })
      queryClient.invalidateQueries({ queryKey: ['inspections-history'] })
      setShowInspectModal(false)
      setSelectedTemplate(null)
      setInspectionResults({})
      setInspectionNotes('')
      setCorrectiveAction('')
    },
  })

  function startInspection(template: InspectionTemplate) {
    setSelectedTemplate(template)
    setInspectionResults({})
    setInspectionNotes('')
    setCorrectiveAction('')
    setShowInspectModal(true)
  }

  function setItemResult(seq: number, field: string, value: string) {
    setInspectionResults(prev => ({
      ...prev,
      [seq]: { ...prev[seq], [field]: value },
    }))
  }

  function submitInspection() {
    if (!selectedTemplate) return
    const items = selectedTemplate.items.map(item => ({
      sequence: item.sequence,
      question: item.question,
      result: inspectionResults[item.sequence]?.result ?? 'NOT_APPLICABLE',
      notes: inspectionResults[item.sequence]?.notes || undefined,
      measurementValue: inspectionResults[item.sequence]?.value
        ? parseFloat(inspectionResults[item.sequence].value)
        : undefined,
    }))

    submitMutation.mutate({
      templateId: selectedTemplate.id,
      items,
      notes: inspectionNotes || undefined,
      correctiveAction: correctiveAction || undefined,
    })
  }

  const hasFailures = Object.values(inspectionResults).some(r => r.result === 'FAIL')
  const allAnswered = selectedTemplate
    ? selectedTemplate.items.filter(i => i.isMandatory).every(i => inspectionResults[i.sequence]?.result)
    : false

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">Safety & Inspections</h1>
          <p className="text-gray-500 text-sm mt-0.5">Fire safety, HACCP, pool, legionella, COSHH</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {(['dashboard', 'history', 'templates'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize',
              tab === t ? 'border-navy-800 text-navy-800' : 'border-transparent text-gray-400 hover:text-gray-600'
            )}
          >
            {t === 'dashboard' ? 'Safety Dashboard' : t === 'history' ? 'Inspection Log' : 'Templates'}
          </button>
        ))}
      </div>

      {/* Dashboard tab */}
      {tab === 'dashboard' && (
        <>
          {logLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-navy-800" />
            </div>
          ) : safetyLog && (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="card py-4">
                  <p className="text-xs text-gray-400">Inspections (30d)</p>
                  <p className="text-2xl font-bold text-navy-800">{safetyLog.summary.totalLast30Days}</p>
                </div>
                <div className="card py-4 border-red-200">
                  <p className="text-xs text-gray-400">Failed</p>
                  <p className={cn('text-2xl font-bold', safetyLog.summary.failedLast30Days > 0 ? 'text-red-600' : 'text-navy-800')}>
                    {safetyLog.summary.failedLast30Days}
                  </p>
                </div>
                <div className="card py-4 border-amber-200">
                  <p className="text-xs text-gray-400">Requires Action</p>
                  <p className={cn('text-2xl font-bold', safetyLog.summary.requiresAction > 0 ? 'text-amber-600' : 'text-navy-800')}>
                    {safetyLog.summary.requiresAction}
                  </p>
                </div>
                <div className="card py-4 border-red-200">
                  <p className="text-xs text-gray-400">Overdue</p>
                  <p className={cn('text-2xl font-bold', safetyLog.summary.overdueCount > 0 ? 'text-red-600' : 'text-navy-800')}>
                    {safetyLog.summary.overdueCount}
                  </p>
                </div>
              </div>

              {/* Template status cards */}
              <h2 className="text-sm font-semibold text-navy-800">Inspection Schedule Status</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {safetyLog.templateStatus.map(({ template: tmpl, lastInspection, isOverdue, isDue, neverCompleted }) => {
                  const cat = CATEGORY_CONFIG[tmpl.category] ?? CATEGORY_CONFIG.GENERAL
                  const Icon = cat.icon
                  return (
                    <div
                      key={tmpl.id}
                      className={cn('card cursor-pointer hover:shadow-card-hover transition-shadow', {
                        'border-red-300 bg-red-50/30': isOverdue,
                        'border-amber-200': isDue && !isOverdue,
                        'border-gray-200': neverCompleted,
                      })}
                      onClick={() => {
                        const t = templates?.find(t => t.id === tmpl.id)
                        if (t) startInspection(t)
                      }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', cat.bg)}>
                            <Icon className={cn('w-4 h-4', cat.color)} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-navy-800">{tmpl.name}</p>
                            <p className="text-xs text-gray-400">{cat.label} {tmpl.frequency ? `\u2022 ${tmpl.frequency}` : ''}</p>
                          </div>
                        </div>
                        {isOverdue ? (
                          <span className="badge bg-red-100 text-red-800">Overdue</span>
                        ) : isDue ? (
                          <span className="badge bg-amber-100 text-amber-800">Due</span>
                        ) : neverCompleted ? (
                          <span className="badge bg-gray-100 text-gray-500">Not Done</span>
                        ) : lastInspection?.overallResult === 'PASS' ? (
                          <span className="badge bg-green-100 text-green-800">Pass</span>
                        ) : lastInspection?.overallResult === 'FAIL' ? (
                          <span className="badge bg-red-100 text-red-800">Failed</span>
                        ) : (
                          <span className="badge bg-amber-100 text-amber-800">Advisory</span>
                        )}
                      </div>

                      {tmpl.regulation && (
                        <p className="text-xs text-gray-400 mb-2">{tmpl.regulation}</p>
                      )}

                      <div className="text-xs text-gray-500 flex items-center gap-3">
                        {lastInspection?.completedAt && (
                          <span>Last: {formatDate(lastInspection.completedAt)}</span>
                        )}
                        {lastInspection?.nextDueDate && (
                          <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                            Next: {formatDate(lastInspection.nextDueDate)}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}

                {safetyLog.templateStatus.length === 0 && (
                  <div className="col-span-full card text-center py-12">
                    <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No inspection templates configured</p>
                    <p className="text-xs text-gray-400 mt-1">Go to the Templates tab to create inspection checklists</p>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* History tab */}
      {tab === 'history' && (
        <>
          <div className="flex gap-3">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="input w-auto min-w-[160px]"
            >
              <option value="">All Categories</option>
              {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>

          {historyLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-navy-800" />
            </div>
          ) : !historyData?.data.length ? (
            <div className="card text-center py-16">
              <ClipboardCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No inspections recorded yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {historyData.data.map((insp) => {
                const cat = CATEGORY_CONFIG[insp.category ?? 'GENERAL'] ?? CATEGORY_CONFIG.GENERAL
                const Icon = cat.icon
                return (
                  <div key={insp.id} className="card">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', cat.bg)}>
                          <Icon className={cn('w-5 h-5', cat.color)} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-navy-800">{insp.templateName}</p>
                          <p className="text-xs text-gray-400">
                            {formatDateTime(insp.completedAt)} {insp.template?.frequency ? `\u2022 ${insp.template.frequency}` : ''}
                          </p>
                        </div>
                      </div>
                      <span className={cn('badge', RESULT_CONFIG[insp.overallResult ?? 'PASS']?.class)}>
                        {RESULT_CONFIG[insp.overallResult ?? 'PASS']?.label}
                      </span>
                    </div>

                    {/* Item results summary */}
                    <div className="flex gap-3 text-xs mb-2">
                      <span className="text-green-600">{insp.summary.passCount} pass</span>
                      {insp.summary.failCount > 0 && <span className="text-red-600 font-medium">{insp.summary.failCount} fail</span>}
                      {insp.summary.advisoryCount > 0 && <span className="text-amber-600">{insp.summary.advisoryCount} advisory</span>}
                    </div>

                    {insp.correctiveAction && (
                      <div className="bg-red-50 rounded-lg p-2 text-xs text-red-700">
                        <strong>Corrective Action:</strong> {insp.correctiveAction}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* Templates tab */}
      {tab === 'templates' && (
        <div className="space-y-4">
          <div className="flex gap-3">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="input w-auto min-w-[160px]"
            >
              <option value="">All Categories</option>
              {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>

          {!templates?.length ? (
            <div className="card text-center py-16">
              <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No templates yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map(tmpl => {
                const cat = CATEGORY_CONFIG[tmpl.category] ?? CATEGORY_CONFIG.GENERAL
                const Icon = cat.icon
                return (
                  <div key={tmpl.id} className="card">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', cat.bg)}>
                          <Icon className={cn('w-4 h-4', cat.color)} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-navy-800">{tmpl.name}</p>
                          <p className="text-xs text-gray-400">{cat.label} {tmpl.frequency ? `\u2022 ${tmpl.frequency}` : ''}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => startInspection(tmpl)}
                        className="btn-primary py-1 px-3 text-xs"
                      >
                        Start
                      </button>
                    </div>
                    {tmpl.regulation && <p className="text-xs text-gray-400 mb-2">{tmpl.regulation}</p>}
                    {tmpl.description && <p className="text-xs text-gray-500 mb-2">{tmpl.description}</p>}
                    <p className="text-xs text-gray-400">{tmpl.items.length} checklist items</p>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Inspection modal */}
      {showInspectModal && selectedTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-navy-800">{selectedTemplate.name}</h2>
                {selectedTemplate.regulation && (
                  <p className="text-xs text-gray-400">{selectedTemplate.regulation}</p>
                )}
              </div>
              <button onClick={() => setShowInspectModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {selectedTemplate.items.map((item) => {
                const current = inspectionResults[item.sequence]
                return (
                  <div key={item.sequence} className={cn(
                    'border rounded-lg p-4',
                    current?.result === 'FAIL' ? 'border-red-300 bg-red-50/30' :
                    current?.result === 'PASS' ? 'border-green-200 bg-green-50/20' :
                    'border-gray-200'
                  )}>
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <p className="text-sm text-navy-800">
                        <span className="text-gray-400 mr-2">{item.sequence}.</span>
                        {item.question}
                        {item.isMandatory && <span className="text-red-500 ml-1">*</span>}
                      </p>
                    </div>

                    {/* Result buttons */}
                    <div className="flex gap-2 mb-2">
                      {['PASS', 'FAIL', 'ADVISORY', 'NOT_APPLICABLE'].map(r => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => setItemResult(item.sequence, 'result', r)}
                          className={cn(
                            'px-3 py-1 rounded-lg text-xs font-medium transition-colors',
                            current?.result === r
                              ? RESULT_CONFIG[r].class
                              : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                          )}
                        >
                          {RESULT_CONFIG[r].label}
                        </button>
                      ))}
                    </div>

                    {/* Measurement input */}
                    {item.type === 'MEASUREMENT' && item.acceptableRange && (
                      <div className="mb-2">
                        <input
                          type="number"
                          step="0.1"
                          value={current?.value ?? ''}
                          onChange={(e) => setItemResult(item.sequence, 'value', e.target.value)}
                          className="input w-32 text-sm"
                          placeholder={`${item.acceptableRange.min ?? ''}–${item.acceptableRange.max ?? ''} ${item.acceptableRange.unit ?? ''}`}
                        />
                        {item.acceptableRange.unit && (
                          <span className="text-xs text-gray-400 ml-2">
                            Range: {item.acceptableRange.min}–{item.acceptableRange.max} {item.acceptableRange.unit}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    {(current?.result === 'FAIL' || current?.result === 'ADVISORY') && (
                      <input
                        value={current?.notes ?? ''}
                        onChange={(e) => setItemResult(item.sequence, 'notes', e.target.value)}
                        className="input text-sm"
                        placeholder={current.result === 'FAIL' ? 'Describe the failure...' : 'Advisory notes...'}
                      />
                    )}
                  </div>
                )
              })}

              {/* Corrective action (if failures) */}
              {hasFailures && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <label className="label text-red-800">Corrective Action Required</label>
                  <textarea
                    value={correctiveAction}
                    onChange={(e) => setCorrectiveAction(e.target.value)}
                    rows={2}
                    className="input resize-none"
                    placeholder="Describe the corrective action to be taken..."
                  />
                  <p className="text-xs text-red-600 mt-1">A work order will be auto-created for failed items</p>
                </div>
              )}

              {/* General notes */}
              <div>
                <label className="label">Inspector Notes</label>
                <textarea
                  value={inspectionNotes}
                  onChange={(e) => setInspectionNotes(e.target.value)}
                  rows={2}
                  className="input resize-none"
                  placeholder="Any general observations..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setShowInspectModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={submitInspection}
                  disabled={submitMutation.isPending}
                  className={hasFailures ? 'btn-danger' : 'btn-primary'}
                >
                  {submitMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                  ) : hasFailures ? (
                    <><XCircle className="w-4 h-4" /> Submit with Failures</>
                  ) : (
                    <><CheckCircle className="w-4 h-4" /> Submit Inspection</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
